import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { getApiErrorMessage } from '@/api/errors';
import { useLatest } from './useLatest';

/**
 * Options for a confirmation request
 */
export interface ConfirmOptions {
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  /**
   * Action to run when confirmed. While it runs the dialog shows a spinner;
   * if it throws, the dialog stays open and shows the error.
   */
  onConfirm?: () => Promise<unknown> | unknown;
  /**
   * Fallback error message for `onConfirm` failures
   * @default 'The operation failed'
   */
  errorMessage?: string;
}

interface ConfirmState {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

export interface UseConfirmResult {
  /**
   * Open the dialog. Resolves true once confirmed (and `onConfirm` succeeded),
   * false when cancelled.
   */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /**
   * The dialog element - render it once in the component
   */
  confirmDialog: ReactElement;
}

/**
 * Promise-based confirmation dialog.
 *
 * @example
 * ```tsx
 * const { confirm, confirmDialog } = useConfirm();
 * const handleDelete = (node: Node) =>
 *   confirm({
 *     title: 'Delete Node',
 *     message: <>Delete <strong>{node.name}</strong>?</>,
 *     confirmLabel: 'Delete',
 *     onConfirm: () => deleteNode(node.id),
 *     errorMessage: 'Failed to delete node',
 *   });
 * return <>{...}{confirmDialog}</>;
 * ```
 */
export function useConfirm(): UseConfirmResult {
  const [state, setState] = useState<ConfirmState | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stateRef = useLatest(state);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // A previous, unanswered request counts as cancelled
      stateRef.current?.resolve(false);
      setError(null);
      setIsPending(false);
      setState({ options, resolve });
    });
  }, [stateRef]);

  const close = useCallback((confirmed: boolean) => {
    stateRef.current?.resolve(confirmed);
    setState(null);
    setIsPending(false);
    setError(null);
  }, [stateRef]);

  const handleConfirm = useCallback(async () => {
    const current = stateRef.current;
    if (!current) return;

    const { onConfirm, errorMessage = 'The operation failed' } = current.options;
    if (!onConfirm) {
      close(true);
      return;
    }

    setIsPending(true);
    setError(null);
    try {
      await onConfirm();
      close(true);
    } catch (err) {
      setIsPending(false);
      setError(getApiErrorMessage(err, errorMessage));
    }
  }, [close, stateRef]);

  const handleCancel = useCallback(() => close(false), [close]);

  const options = state?.options;

  const confirmDialog = (
    <ConfirmDialog
      isOpen={state !== null}
      title={options?.title ?? ''}
      message={options?.message ?? ''}
      confirmLabel={options?.confirmLabel}
      cancelLabel={options?.cancelLabel}
      variant={options?.variant}
      isPending={isPending}
      error={error}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, confirmDialog };
}
